import React from "react";
import { useQuery, gql } from "@apollo/client";
import MapPicker from "./MapPicker";
import { SAUNA_TYPES } from "../utils/saunaTypes";

const AMENITIES_QUERY = gql`
  query AmenitiesForForm {
    allAmenities(orderBy: DISPLAY_ORDER_ASC) {
      nodes { id label }
    }
  }
`;

export interface SaunaFields {
  name: string;
  description: string;
  addressLine: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  capacity: string;
  pricePerHourCents: string;
  lat: number | null;
  lng: number | null;
  saunaType: string | null;
  amenityIds: number[];
  requiresApproval: boolean;
}

export const emptySaunaFields: SaunaFields = {
  name: "", description: "", addressLine: "", city: "", state: "",
  postalCode: "", country: "USA", capacity: "1", pricePerHourCents: "",
  lat: null, lng: null,
  saunaType: null, amenityIds: [],
  requiresApproval: true,
};

interface Props {
  fields: SaunaFields;
  onChange: (fields: SaunaFields) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string | null;
  submitLabel: string;
  extraActions?: React.ReactNode;
}

export default function SaunaForm({ fields, onChange, onSubmit, loading, error, submitLabel, extraActions }: Props) {
  const { data: amenityData } = useQuery(AMENITIES_QUERY);
  const amenities: { id: number; label: string }[] = amenityData?.allAmenities?.nodes ?? [];

  const set = (key: keyof SaunaFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange({ ...fields, [key]: e.target.value });

  const toggleAmenity = (id: number) => {
    const next = fields.amenityIds.includes(id)
      ? fields.amenityIds.filter((a) => a !== id)
      : [...fields.amenityIds, id];
    onChange({ ...fields, amenityIds: next });
  };

  return (
    <form onSubmit={onSubmit}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="Name *">
          <input type="text" value={fields.name} onChange={set("name")} required style={inputStyle} />
        </Field>
        <Field
          label="Sauna description"
          hint="Tell guests how they ought to use your sauna — parking, prep time, house rules, anything they should know."
        >
          <textarea
            value={fields.description}
            onChange={set("description")}
            rows={5}
            placeholder="Traditional wood-fired sauna with cedar paneling. Please park in the lower driveway. We'll start the sauna 2 hours before your booking."
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </Field>
        <Field label="Sauna type">
          <select
            value={fields.saunaType ?? ""}
            onChange={(e) => onChange({ ...fields, saunaType: e.target.value || null })}
            style={inputStyle}
          >
            <option value="">— select —</option>
            {SAUNA_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>
        {amenities.length > 0 && (
          <Field label="Amenities">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
              {amenities.map((a) => {
                const checked = fields.amenityIds.includes(a.id);
                return (
                  <label
                    key={a.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 13,
                      border: `1px solid ${checked ? "#2563EB" : "#d1d5db"}`,
                      background: checked ? "#eff6ff" : "#fff",
                      color: checked ? "#1d4ed8" : "#374151",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAmenity(a.id)}
                      style={{ margin: 0 }}
                    />
                    {a.label}
                  </label>
                );
              })}
            </div>
          </Field>
        )}
        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={fields.requiresApproval}
              onChange={e => onChange({ ...fields, requiresApproval: e.target.checked })}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                Require my approval before bookings are confirmed
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                Guests send a request and you decide whether to accept. Use this if you want to vet who's coming to your property.
              </div>
            </div>
          </label>
        </div>

        <Field label="Address">
          <input type="text" value={fields.addressLine} onChange={set("addressLine")} placeholder="123 Sauna Lane" style={inputStyle} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <Field label="City">
            <input type="text" value={fields.city} onChange={set("city")} style={inputStyle} />
          </Field>
          <Field label="State">
            <input type="text" value={fields.state} onChange={set("state")} placeholder="MN" style={inputStyle} />
          </Field>
          <Field label="Zip">
            <input type="text" value={fields.postalCode} onChange={set("postalCode")} style={inputStyle} />
          </Field>
        </div>
        <Field label="Country">
          <input type="text" value={fields.country} onChange={set("country")} style={inputStyle} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Capacity (people) *">
            <input type="number" min={1} value={fields.capacity} onChange={set("capacity")} required style={inputStyle} />
          </Field>
          <Field label="Price per hour ($) *">
            <input
              type="number"
              min={0}
              step="0.01"
              value={fields.pricePerHourCents === "" ? "" : (Number(fields.pricePerHourCents) / 100).toString()}
              onChange={(e) => onChange({ ...fields, pricePerHourCents: Math.round(parseFloat(e.target.value || "0") * 100).toString() })}
              placeholder="e.g. 25.00"
              required
              style={inputStyle}
            />
          </Field>
        </div>
        <Field label="Map pin — click to place">
          <MapPicker
            lat={fields.lat}
            lng={fields.lng}
            onChange={(lat, lng) => onChange({ ...fields, lat, lng })}
          />
          {fields.lat !== null && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              {fields.lat.toFixed(5)}, {fields.lng!.toFixed(5)}
            </div>
          )}
        </Field>
      </div>

      {error && <p style={{ color: "#ef4444", marginTop: 16 }}>{error}</p>}

      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <button type="submit" disabled={loading} style={primaryBtn}>
          {loading ? "Saving..." : submitLabel}
        </button>
        {extraActions}
      </div>
    </form>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: hint ? 2 : 4, color: "#374151" }}>
        {label}
      </label>
      {hint && (
        <p style={{ margin: "0 0 6px", fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>{hint}</p>
      )}
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 12px", border: "1px solid #d1d5db",
  borderRadius: 6, fontSize: 14, boxSizing: "border-box",
};

const primaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
